import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuth = async () => {
    // The JWT lives in an httpOnly cookie (not readable by JS). We keep a
    // non-sensitive copy of the user profile in localStorage only for instant
    // UI hydration, then confirm the session against the server via /auth/me.
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (error) {
        localStorage.removeItem('user');
      }
    }
    // Confirm the cookie session (and refresh roles). Clears state on 401.
    await refreshUserData();
    setLoading(false);
  };

  const refreshUserData = async () => {
    try {
      const response = await api.get('/auth/me');
      if (response.data && response.data.user) {
        const refreshedUser = response.data.user;
        localStorage.setItem('user', JSON.stringify(refreshedUser));
        setUser(refreshedUser);
      }
    } catch (error) {
      // No valid cookie session — ensure we are logged out locally.
      if (error.response?.status === 401) {
        localStorage.removeItem('user');
        setUser(null);
      }
      // Other errors (e.g. network/server down): keep any hydrated user.
    }
  };

  const login = async (identifier, password) => {
    try {
      const response = await api.post('/auth/login', { identifier, password });
      // The server sets the JWT in an httpOnly cookie; we only persist the
      // non-sensitive user profile for UI hydration.
      const { user } = response.data;
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed'
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await api.post('/auth/register', userData);
      const { user } = response.data;
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Registration failed'
      };
    }
  };

  const logout = async () => {
    try {
      // Ask the server to clear the httpOnly auth cookie.
      await api.post('/auth/logout');
    } catch (error) {
      // Ignore network errors — still clear local state below.
    }
    localStorage.removeItem('user');
    setUser(null);
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  // RBAC helpers
  const hasRole = useCallback((roleName) => {
    if (!user) return false;
    const roles = user.roles || [];
    return roles.includes(roleName);
  }, [user]);

  const hasAnyRole = useCallback((roleNames) => {
    if (!user) return false;
    const roles = user.roles || [];
    return roleNames.some(r => roles.includes(r));
  }, [user]);

  const hasPermission = useCallback((permissionName) => {
    if (!user) return false;
    const permissions = user.permissions || [];
    return permissions.includes(permissionName);
  }, [user]);

  const hasAnyPermission = useCallback((permissionNames) => {
    if (!user) return false;
    const permissions = user.permissions || [];
    return permissionNames.some(p => permissions.includes(p));
  }, [user]);

  const getPrimaryRole = useCallback(() => {
    if (!user) return null;
    const roles = user.roles || [];
    const hierarchy = [
      'organisation_management',
      'directors',
      'principal',
      'admin',
      'dean',
      'hr_manager',
      'student_mentors',
      'student_coordinators',
      'teachers',
      'teacher',
      'student',
      'parent'
    ];
    return hierarchy.find(r => roles.includes(r)) || user.role || 'student';
  }, [user]);

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    updateUser,
    isAuthenticated: !!user,
    hasRole,
    hasAnyRole,
    hasPermission,
    hasAnyPermission,
    getPrimaryRole
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};